import React, { useEffect, useState } from 'react';

import { useDebounce, useLanguage } from '../../../hooks';
import { useLocationsInfiniteQuery } from '../../../queries';
import { cn } from '../../../utils/utils';
import { Checkbox } from '../../shadcn-ui/checkbox';
import { FilterEmptyState } from '../../uiComponents/CommonComponents/FilterEmptyState';

interface LegacyLocationsTreeContentProps {
  searchQuery: string;
  selectedLocations: number[];
  onSelect: (locationId: number) => void;
  multipleSelection: boolean;
  customSearchNoResultsText?: string;
}

export function LegacyLocationsTreeContent({
  searchQuery,
  selectedLocations,
  onSelect,
  multipleSelection,
  customSearchNoResultsText,
}: LegacyLocationsTreeContentProps) {
  const lang = useLanguage();

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const debouncedSetQuery = useDebounce(() => setDebouncedSearchQuery(searchQuery), 500);

  useEffect(() => {
    debouncedSetQuery();
  }, [searchQuery]);

  const selectedLocationIds = new Set(selectedLocations);

  const {
    data: locationsQuery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useLocationsInfiniteQuery(debouncedSearchQuery);

  const locations = locationsQuery?.pages.flatMap((locationPage) => locationPage.locations) ?? [];

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  if (isLoading) {
    return (
      <li className='px-3 py-2 text-sm text-gray-500'>
        {lang.commonComponents.locationSelector.loading}
      </li>
    );
  }

  if (locations.length === 0) {
    if (customSearchNoResultsText && debouncedSearchQuery.trim()) {
      return (
        <FilterEmptyState
          text={customSearchNoResultsText}
          variant='search'
        />
      );
    }
    return <FilterEmptyState text={lang.commonComponents.locationSelector.noLocationsFound} />;
  }

  return (
    <div className='flex-1 overflow-auto'>
      <ul
        className='max-h-64 overflow-auto border border-gray-200 rounded-md'
        onScroll={handleScroll}
      >
        {locations.map((location) => {
          const isSelected = selectedLocationIds.has(location.id);
          const showSelectedStyle = !multipleSelection && isSelected;
          return (
            <li
              key={location.id}
              className={cn(
                'px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2',
                showSelectedStyle && 'bg-primary/10',
              )}
              onClick={() => onSelect(location.id)}
            >
              {multipleSelection && (
                <Checkbox
                  checked={isSelected}
                  className={cn(
                    'data-[state=checked]:bg-primary data-[state=checked]:border-primary',
                    !isSelected && 'opacity-50',
                  )}
                />
              )}
              {location.name}
              {location.code ? ` [${location.code}]` : ''}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
